// Fase 10 (Tematización visual). `main.css` ya está construido 100% sobre
// custom properties CSS (`--sp-color-primario`, `--sp-color-acento`, etc.
// — ver `:root` en styles/main.css), así que "tematizar" una tienda es
// sobrescribir esas variables en tiempo de ejecución con los colores que
// eligió el comerciante, sin tocar ni una regla CSS existente.
//
// Si la tienda no configuró un tema (`tema.color_primario`/`color_secundario`
// vienen `null` — ver datos-publicados.js), esta función no hace nada: las
// declaraciones de `:root` en el CSS siguen aplicando tal cual, cero
// cambio de comportamiento respecto a antes de esta fase.

/**
 * Oscurece un color hexadecimal un porcentaje fijo — se usa para derivar
 * `--sp-color-primario-oscuro` (estado hover/activo de botones) a partir
 * del único color primario que elige el comerciante, en vez de pedirle que
 * elija dos tonos coordinados a mano.
 *
 * @param {string} colorHex - `#rrggbb`
 * @param {number} porcentaje - 0 a 1 (0.2 = 20% más oscuro)
 * @returns {string} `#rrggbb`
 */
export function oscurecerColor(colorHex, porcentaje) {
  const numero = parseInt(colorHex.slice(1), 16);
  const canal = (desplazamiento) => {
    const valor = (numero >> desplazamiento) & 0xff;
    return Math.max(0, Math.round(valor * (1 - porcentaje)));
  };

  const componente = (valor) => valor.toString(16).padStart(2, '0');
  return `#${componente(canal(16))}${componente(canal(8))}${componente(canal(0))}`;
}

/**
 * Devuelve el color de primer plano (texto/iconos) con mayor contraste sobre
 * un color de fondo HEX dado, según luminosidad relativa (WCAG). Se usa para
 * que el contenido del header siga siendo legible cuando su fondo hereda el
 * `color_primario` de la tienda: si el primario es oscuro → primer plano
 * claro; si es claro → primer plano oscuro. No hardcodea nada: el propio
 * algoritmo decide `#ffffff` o `#13201b` según cuál ofrezca más contraste.
 *
 * @param {string} colorHex - `#rrggbb` ya validado
 * @returns {string} `#ffffff` (sobre fondos oscuros) o `#13201b` (sobre claros)
 */
export function colorContraste(colorHex) {
  const numero = parseInt(colorHex.slice(1), 16);
  const canal = (desplazamiento) => ((numero >> desplazamiento) & 0xff) / 255;
  const lineal = (valor) =>
    valor <= 0.03928 ? valor / 12.92 : Math.pow((valor + 0.055) / 1.055, 2.4);

  const r = lineal(canal(16));
  const g = lineal(canal(8));
  const b = lineal(canal(0));
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  const ratio = (l1, l2) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  const frenteClaro = '#ffffff';
  const frenteOscuro = '#13201b';
  return ratio(lum, 1) >= ratio(lum, 0) ? frenteClaro : frenteOscuro;
}

const REGEX_COLOR_HEX = /^#[0-9a-fA-F]{6}$/;

// Componente RGBA de un hex para texto secundario/sutil (`--sp-header-muted`).
// Devuelve `rgb(r g b / a)` para poder usar `color-mix` con el mismo lenguaje
// que el resto del storefront.
function colorConTransparencia(colorHex, alfa) {
  const numero = parseInt(colorHex.slice(1), 16);
  const rojo = (numero >> 16) & 0xff;
  const verde = (numero >> 8) & 0xff;
  const azul = numero & 0xff;
  return `rgb(${rojo} ${verde} ${azul} / ${alfa})`;
}

const FUENTES_PERMITIDAS = {
  inter: "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  system: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
  rounded: "'Nunito Sans', Inter, ui-sans-serif, system-ui, sans-serif",
};

const SOMBRAS = {
  ninguna: 'none',
  suave: '0 12px 30px rgb(16 38 58 / 0.1)',
  media: '0 18px 45px rgb(16 38 58 / 0.16)',
  fuerte: '0 24px 70px rgb(16 38 58 / 0.22)',
};

const ESPACIADOS = {
  compacto: '0.88',
  normal: '1',
  amplio: '1.14',
};

function aplicarColor(raiz, variable, valor) {
  if (valor && REGEX_COLOR_HEX.test(valor)) raiz.setProperty(variable, valor);
}

// Actualiza `meta[name="theme-color"]` del documento que está mostrando la
// tienda para pintar la zona superior EXTERNA del navegador (hora, señal,
// WiFi y batería) con un color neutro fijo BLANCO, independiente del tema.
// Esta zona es chrome del navegador y NO es parte del header del DOM de la
// tienda: el header real (franja con menú/logo/carrito/búsqueda/categorías)
// conserva intacta su propia lógica (color_header y contraste adaptativo).
// Se MUTA el `content` del meta existente (creándolo solo si no existe) y NO
// se elimina/reinserta el nodo: en Chrome/Android el pintado de la barra lo
// dispara el cambio del atributo en sitio, mientras que sustituir el nodo por
// uno nuevo suele ignorarse y deja la barra con el color neutro del HTML.
const COLOR_BARRA_NAVEGADOR = '#ffffff';
function sincronizarThemeColor(doc) {
  if (!doc) return;
  const head = doc.head;
  if (!head) return;
  let meta = doc.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement('meta');
    meta.name = 'theme-color';
    head.appendChild(meta);
  }
  meta.content = COLOR_BARRA_NAVEGADOR;
}

function aplicarLongitud(raiz, variable, valor) {
  if (typeof valor !== 'string' && typeof valor !== 'number') return;
  const limpio = String(valor).trim();
  if (/^\d+(\.\d+)?(px|rem)$/.test(limpio)) raiz.setProperty(variable, limpio);
  if (/^\d+(\.\d+)?$/.test(limpio)) raiz.setProperty(variable, `${limpio}px`);
}

// Tokens del lenguaje visual compartido del Editor (Parte 2 / Motor de
// temas). Son los mismos nombres que `aplicarTemaPreview` escribe en la
// preview del Panel Cliente (`estilos-preview.css` usa `--sp-primario`,
// `--sp-botones`, `--sp-radio`, ...), asi EDITOR = PREVIEW = STOREFRONT
// comparten el mismo vocabulario y el CSS nuevo del storefront se construye
// sobre estos. Las variables legacy (`--color-*`, `--sp-color-*`,
// `--radius`, `--shadow`, `--spacing-scale`) se siguen escribiendo para que
// el CSS existente y los tests que las consultan sigan funcionando.
function aplicarTemaEditor(raiz, tema) {
  const revisar = (variable, valor) => {
    if (valor && REGEX_COLOR_HEX.test(String(valor))) raiz.setProperty(variable, valor);
  };

  revisar('--sp-primario', tema?.color_primario);
  revisar('--sp-secundario', tema?.color_secundario);
  revisar('--sp-botones', tema?.color_botones);
  revisar('--sp-botones-hover', tema?.color_botones_hover);
  revisar('--sp-texto', tema?.color_texto);
  revisar('--sp-header', tema?.color_header);
  revisar('--sp-footer', tema?.color_footer);
  revisar('--sp-enlaces', tema?.color_enlaces);
  revisar('--sp-fondo', tema?.color_fondo);
  revisar('--sp-tarjetas', tema?.color_tarjetas);

  if (tema?.color_primario && REGEX_COLOR_HEX.test(tema.color_primario)) {
    // Los campos dedicados `color_botones_hover`/`color_enlaces` del panel
    // tienen prioridad; solo se deriva del primario cuando no vienen seteado
    // (compatibilidad con temas antiguos que no los guardan).
    if (!tema?.color_botones_hover || !REGEX_COLOR_HEX.test(String(tema.color_botones_hover))) {
      raiz.setProperty('--sp-botones-hover', oscurecerColor(tema.color_primario, 0.14));
    }
    if (!tema?.color_enlaces || !REGEX_COLOR_HEX.test(String(tema.color_enlaces))) {
      raiz.setProperty('--sp-enlaces', tema.color_primario);
    }
  }

  if (typeof tema?.radio_bordes !== 'undefined' && tema.radio_bordes !== null) {
    raiz.setProperty('--sp-radio', `${tema.radio_bordes}px`);
  }

  if (SOMBRAS[tema?.sombras]) raiz.setProperty('--sp-sombra', SOMBRAS[tema.sombras]);
  if (ESPACIADOS[tema?.espaciado]) {
    raiz.setProperty('--sp-espaciado-mult', ESPACIADOS[tema.espaciado]);
  }
}

/**
 * Aplica el tema visual publicado a las custom properties de un documento.
 * Escribe tanto el vocabulario legacy del storefront (Fase 10) como los
 * tokens del Editor (`--sp-primario`, etc.), de forma idempotente.
 *
 * `raiz` permite fijar el elemento raíz del documento objetivo (por
 * defecto el del documento actual). El preview del panel lo reutiliza
 * para pintar el MISMO tema dentro de su iframe (EDITOR = PREVIEW =
 * STOREFRONT, sin duplicar el mapeo de tokens).
 *
 * `doc` es el documento cuyo `meta[name="theme-color"]` se fija a un color
 * neutro fijo blanco (por defecto el documento actual). La storefront pública
 * lo deja por defecto y así pinta la zona superior EXTERNA del navegador (barra
 * de hora/señal/WiFi/batería) en blanco, sin tocar el header real de la tienda;
 * el preview del panel le pasa su `doc` (el iframe), donde escribir el
 * theme-color es inocuo. No se compara `raiz` con el documento actual: la
 * sincronización se dirige siempre al documento que está mostrando el tema.
 *
 * @param {Record<string, unknown> | undefined} tema
 * @param {CSSStyleDeclaration} [raiz] - estilos del <html> del documento destino
 * @param {Document} [doc] - documento cuyo theme-color hay que sincronizar
 */
export function aplicarTemaVisual(tema, raiz = document.documentElement.style, doc = document) {
  if (tema?.color_primario && REGEX_COLOR_HEX.test(tema.color_primario)) {
    const colorPrimario = tema.color_primario;
    raiz.setProperty('--sp-color-primario', colorPrimario);
    raiz.setProperty('--color-primary', colorPrimario);
    raiz.setProperty('--sp-color-primario-oscuro', oscurecerColor(colorPrimario, 0.18));
    raiz.setProperty('--color-button-hover', oscurecerColor(colorPrimario, 0.18));

    // La franja superior (header) acompaña al color primario de la tienda en
    // vez de transparentar el `color_fondo` (que queda independiente). Solo
    // actúa como respaldo: si el comerciante eligió un `color_header` propio
    // (o lo cambia después), ese valor manda y este bloque deja de aplicarse.
    const headerPropio = tema?.color_header && REGEX_COLOR_HEX.test(String(tema.color_header));
    if (!headerPropio) {
      raiz.setProperty('--sp-header', colorPrimario);
      raiz.setProperty('--color-header', colorPrimario);

      // Primer plano adaptativo del header: claro u oscuro según la
      // luminosidad del color primario (ver `colorContraste`). Como estos
      // tokens SOLO se escriben en el respaldo del primario, el CSS scoped al
      // header los usa con `fallback` y no alteran el texto global de la
      // tienda (body, títulos, footer, etc.) ni el header con `color_header`
      // propio.
      const contraste = colorContraste(colorPrimario);
      raiz.setProperty('--sp-header-foreground', contraste);
      raiz.setProperty('--sp-header-muted', colorConTransparencia(contraste, 0.72));
    } else {
      // El comerciante eligió color_header: limpiar el foreground adaptativo
      // por si quedó de una aplicación previa en modo respaldo.
      raiz.removeProperty('--sp-header-foreground');
      raiz.removeProperty('--sp-header-muted');
    }

    // Zona superior EXTERNA del navegador (barra de hora/señal/WiFi/batería):
    // se deja neutra/blanca fija, independiente del tema y del header del DOM.
    // Es chrome del navegador, no una pieza de la tienda, por lo que no se le
    // aplica color_primario ni color_header (el header real conserva su
    // lógica intacta). Se fija siempre tras aplicar el tema real, nunca en el
    // estado de carga que trae el valor estático de index.html.
    sincronizarThemeColor(doc);
  }

  if (tema?.color_secundario && REGEX_COLOR_HEX.test(tema.color_secundario)) {
    raiz.setProperty('--sp-color-acento', tema.color_secundario);
    raiz.setProperty('--color-secondary', tema.color_secundario);
  }

  aplicarColor(raiz, '--color-button', tema?.color_botones);
  aplicarColor(raiz, '--color-button-hover', tema?.color_botones_hover);
  aplicarColor(raiz, '--color-text', tema?.color_texto);
  aplicarColor(raiz, '--color-header', tema?.color_header);
  aplicarColor(raiz, '--color-footer', tema?.color_footer);
  aplicarColor(raiz, '--color-link', tema?.color_enlaces);
  aplicarColor(raiz, '--color-background', tema?.color_fondo);
  aplicarColor(raiz, '--color-surface', tema?.color_tarjetas);

  aplicarColor(raiz, '--sp-color-texto', tema?.color_texto);
  aplicarColor(raiz, '--sp-color-tinta', tema?.color_texto);
  aplicarColor(raiz, '--sp-color-fondo', tema?.color_fondo);
  aplicarColor(raiz, '--sp-color-superficie', tema?.color_tarjetas);

  aplicarLongitud(raiz, '--radius', tema?.radio_bordes);
  aplicarLongitud(raiz, '--sp-radio', tema?.radio_bordes);

  const fuente = FUENTES_PERMITIDAS[tema?.tipografia];
  if (fuente) {
    raiz.setProperty('--font-family', fuente);
    raiz.setProperty('--sp-fuente', fuente);
  }

  if (SOMBRAS[tema?.sombras]) {
    raiz.setProperty('--shadow', SOMBRAS[tema.sombras]);
    raiz.setProperty('--sp-sombra', SOMBRAS[tema.sombras]);
  }

  if (ESPACIADOS[tema?.espaciado]) {
    raiz.setProperty('--spacing-scale', ESPACIADOS[tema.espaciado]);
  }

  aplicarTemaEditor(raiz, tema);
}

/**
 * Favicon por tienda (`empresa_perfil.favicon_url`). `index.html` no trae
 * uno fijo por empresa (es un único build para todas las tiendas), así que
 * se resuelve en cliente: si la tienda subió uno, se reemplaza el
 * `<link rel="icon">`; si no, el navegador usa el que ya declara
 * `index.html` (el genérico de la plataforma).
 *
 * @param {string | null | undefined} faviconUrl
 */
export function aplicarFavicon(faviconUrl) {
  if (!faviconUrl) return;

  let enlace = document.querySelector('link[rel="icon"]');
  if (!enlace) {
    enlace = document.createElement('link');
    enlace.rel = 'icon';
    document.head.appendChild(enlace);
  }
  enlace.href = faviconUrl;
}
